// X.13 (client-side feature-flag governance) — canonical storage layer for
// every `vharta.*` client override (unified-canvas flags, cookie-auth mode,
// dev_mode, …).
//
// Found via P1.20/P1.24: a bare `localStorage['vharta.unified_canvas_stategraphs']
// = 'true'` — set once, probably for a one-off test — silently ran the
// platform owner on the unfinished unified canvas for days. Every "still
// broken live" report on P1.20 turned out to be that flag, invisible because
// nothing surfaced it, and permanent because nothing expired it. This module
// exists to make that class of bug structurally harder:
//   - every write is timestamped ({value, setAt}) and expires after 14 days;
//     a legacy bare string (set before this module existed, or by hand in
//     devtools) is treated as "set now" the first time it's READ, so it gets
//     a fresh clock from the moment we notice it rather than an instant
//     retroactive expiry.
//   - a one-off override can live in sessionStorage only (via `?flags=`,
//     see below) instead of leaking into every future session. `?flags=` is
//     attacker-controlled (a URL), so it can only ever set a small,
//     hardcoded ALLOWLIST of purely cosmetic flags — see
//     URL_SETTABLE_FLAG_ALLOWLIST below. It can never set an auth/security-
//     relevant flag (cookie_auth, dev_mode), regardless of `&persist=1`.
//   - every write bumps a module-level version counter so
//     useSyncExternalStore-based readers (see useUnifiedCanvasFlag.ts) update
//     without a full reload — clearing a flag takes effect immediately.
//   - active overrides are queryable (getActiveVhartaOverrides) for the
//     dev-only panel in CanvasFlagBadge and for anyone debugging a live
//     report (see web/src/.claude.md's flag-dump rule).
//
// Callers should use setVhartaFlag/getVhartaFlag/clearVhartaFlag rather than
// touching localStorage/sessionStorage directly — see useUnifiedCanvasFlag.ts
// and services/cookieAuthFlag.ts for the two current call sites.

export interface VhartaFlagRecord {
  value: string;
  setAt: number;
}

export interface VhartaFlagOverride {
  key: string;
  value: string;
  scope: 'session' | 'local';
  /** null for a legacy value that hasn't been migrated to a record yet. */
  setAt: number | null;
}

const VHARTA_PREFIX = 'vharta.';
const FLAG_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// Storage can't be reliably enumerated (Storage.key()/.length aren't
// implemented by every environment we run in — notably the shared jsdom
// localStorage mock in src/test/setup.ts, which exists to satisfy
// ApiClient's synchronous module-load-time read and only implements
// getItem/setItem/removeItem/clear). We maintain our own index of the keys
// *this module* has written or observed instead of depending on
// enumeration, so getActiveVhartaOverrides/clearAllVhartaOverrides work
// identically in real browsers and in tests.
const INDEX_KEY = `${VHARTA_PREFIX}__flag_index__`;

function readIndex(storage: Storage): string[] {
  try {
    const raw = storage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function writeIndex(storage: Storage, keys: string[]): void {
  try {
    storage.setItem(INDEX_KEY, JSON.stringify(Array.from(new Set(keys))));
  } catch {
    // storage unavailable/full — index is best-effort, never load-bearing
    // for getVhartaFlag's own precedence logic.
  }
}

function addToIndex(storage: Storage, key: string): void {
  if (key === INDEX_KEY) return;
  const idx = readIndex(storage);
  if (!idx.includes(key)) writeIndex(storage, [...idx, key]);
}

function removeFromIndex(storage: Storage, key: string): void {
  const idx = readIndex(storage);
  const next = idx.filter((k) => k !== key);
  if (next.length !== idx.length) writeIndex(storage, next);
}

function parseRecord(raw: string): VhartaFlagRecord | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof (parsed as VhartaFlagRecord).value === 'string' &&
      typeof (parsed as VhartaFlagRecord).setAt === 'number'
    ) {
      return parsed as VhartaFlagRecord;
    }
  } catch {
    // Not JSON — legacy bare value (e.g. the literal string 'true').
  }
  return null;
}

// ── Reactivity ────────────────────────────────────────────────────────────
// A module-level version counter + tiny pub/sub, consumed via
// useSyncExternalStore by useUnifiedCanvasFlag/useVhartaFlagValue. This is
// what lets clearing a flag (e.g. the badge's "Switch to standard canvas"
// action) take effect without `location.reload()` — the previous
// `useMemo(..., [])` in useUnifiedCanvasFlag.ts read the flag exactly once
// per component lifetime.
let flagVersion = 0;
const listeners = new Set<() => void>();

function bumpFlagVersion(): void {
  flagVersion += 1;
  listeners.forEach((l) => l());
}

export function subscribeVhartaFlags(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export function getVhartaFlagVersion(): number {
  return flagVersion;
}

// ── URL-settable allowlist (security) ───────────────────────────────────
// A `?flags=` query param is attacker-controlled: anyone who gets a victim
// to click a link can set it, so it must NEVER be able to flip a
// security/auth-relevant flag. Security review of the initial cut of this
// module (0d6b94c4a) flagged exactly this — an unfiltered `?flags=` would
// have let `?flags=cookie_auth&persist=1` PERSISTENTLY flip cookie-only auth
// mode for a victim (cookieAuthFlag.ts reads through getVhartaFlag below),
// and `?flags=dev_mode` would have exposed the dev-only overrides panel to
// anyone who followed the link — regardless of `&persist=1` being present.
//
// Only the three purely cosmetic unified-canvas rollout flags are safe to
// let a URL set. Every other `vharta.*` flag — `cookie_auth` and `dev_mode`
// explicitly, and ANY future flag by default — is DENIED, silently no-op'd
// (with a console.warn), regardless of `&persist=1`. Adding a flag here is a
// deliberate, reviewed decision: default-deny, not default-allow. An
// auth/security-relevant flag (anything gating what requests carry, what
// gets persisted to storage, or what internal tooling is exposed) must
// never be added to this set.
export const URL_SETTABLE_FLAG_ALLOWLIST: ReadonlySet<string> = new Set([
  'vharta.unified_canvas_stategraphs',
  'vharta.unified_canvas_workflows',
  'vharta.unified_canvas_chains',
]);

// Explicitly documented as unsettable via URL, even though the allowlist
// above is already default-deny — this list exists so the intent is visible
// at the call site and grep-able, and so a test can assert against it
// directly, not because it changes behaviour (any key not in
// URL_SETTABLE_FLAG_ALLOWLIST is denied regardless of whether it's named
// here).
export const URL_DENYLIST_DOCUMENTATION: ReadonlySet<string> = new Set([
  'vharta.cookie_auth',
  'vharta.dev_mode',
]);

// ── Query-param bootstrap: ?flags=a,b (session-scoped) / &persist=1 ────────
// Applied lazily (once) the first time any flag is read, rather than at
// module import time, so it works the same whether this module is imported
// before or after the router has parsed the URL, and so tests that never
// touch window.location.search don't pay for it.
let queryParamsApplied = false;

function ensureQueryParamsApplied(): void {
  if (queryParamsApplied) return;
  queryParamsApplied = true;
  if (typeof window === 'undefined' || !window.location) return;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  const flags = params.get('flags');
  if (!flags) return;
  const persist = params.get('persist') === '1';
  for (const raw of flags.split(',')) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.startsWith(VHARTA_PREFIX) ? trimmed : `${VHARTA_PREFIX}${trimmed}`;
    if (!URL_SETTABLE_FLAG_ALLOWLIST.has(key)) {
      console.warn(
        `[vhartaFlags] ignoring ?flags= entry "${trimmed}" — "${key}" is not on the URL-settable allowlist (see URL_SETTABLE_FLAG_ALLOWLIST in vhartaFlags.ts). Auth/security-relevant flags can never be set via URL.`,
      );
      continue;
    }
    setVhartaFlag(key, 'true', { persist });
  }
}

// ── Read ─────────────────────────────────────────────────────────────────

/**
 * Resolve a `vharta.*` flag's current string value, or null if unset/expired.
 * Precedence: session override (set via `?flags=`, no `&persist=1`) →
 * localStorage record (expires 14 days after setAt) → null.
 *
 * A legacy bare string (not a `{value,setAt}` JSON record — i.e. written
 * directly via `localStorage.setItem('vharta.x', 'true')` before this module
 * existed, or by hand in devtools) is returned as-is on first read AND
 * migrated in place to a timestamped record (setAt = now), so it starts a
 * fresh 14-day clock from the moment it's noticed rather than expiring
 * instantly on the next read.
 */
export function getVhartaFlag(key: string): string | null {
  ensureQueryParamsApplied();

  try {
    const raw = sessionStorage.getItem(key);
    if (raw != null) {
      const rec = parseRecord(raw);
      return rec ? rec.value : raw; // session overrides don't expire — they die with the tab.
    }
  } catch {
    // sessionStorage unavailable (SSR, sandboxed iframe, privacy mode).
  }

  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw == null) return null;

  const rec = parseRecord(raw);
  if (rec) {
    if (Date.now() - rec.setAt > FLAG_EXPIRY_MS) {
      try {
        localStorage.removeItem(key);
        removeFromIndex(localStorage, key);
      } catch {
        // best-effort cleanup
      }
      return null;
    }
    return rec.value;
  }

  // Legacy bare value — migrate to a timestamped record, set-now.
  try {
    const migrated: VhartaFlagRecord = { value: raw, setAt: Date.now() };
    localStorage.setItem(key, JSON.stringify(migrated));
    addToIndex(localStorage, key);
  } catch {
    // migration failed — still honour the raw value for this read.
  }
  return raw;
}

// ── Write ────────────────────────────────────────────────────────────────

export interface SetVhartaFlagOptions {
  /**
   * true = write to localStorage (survives across sessions/tabs, subject to
   * the 14-day expiry). false/omitted = sessionStorage only (dies with the
   * tab, never expires on its own). Mirrors `?flags=x&persist=1`.
   */
  persist?: boolean;
}

export function setVhartaFlag(key: string, value: string, opts: SetVhartaFlagOptions = {}): void {
  const record: VhartaFlagRecord = { value, setAt: Date.now() };
  const json = JSON.stringify(record);
  try {
    if (opts.persist) {
      localStorage.setItem(key, json);
      addToIndex(localStorage, key);
      // A persisted write supersedes any session-scoped override of the
      // same key — otherwise getVhartaFlag's session-first precedence would
      // keep serving the stale session value.
      sessionStorage.removeItem(key);
      removeFromIndex(sessionStorage, key);
    } else {
      sessionStorage.setItem(key, json);
      addToIndex(sessionStorage, key);
    }
  } catch {
    // storage unavailable — the flag simply won't take effect.
  }
  bumpFlagVersion();
}

export function clearVhartaFlag(key: string): void {
  try {
    localStorage.removeItem(key);
    removeFromIndex(localStorage, key);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(key);
    removeFromIndex(sessionStorage, key);
  } catch {
    // ignore
  }
  bumpFlagVersion();
}

// ── Dev-panel introspection ─────────────────────────────────────────────

/** Active (non-expired) vharta.* overrides across both storages, session first. */
export function getActiveVhartaOverrides(): VhartaFlagOverride[] {
  const out: VhartaFlagOverride[] = [];
  const seen = new Set<string>();

  try {
    for (const key of readIndex(sessionStorage)) {
      const raw = sessionStorage.getItem(key);
      if (raw == null) {
        removeFromIndex(sessionStorage, key);
        continue;
      }
      const rec = parseRecord(raw);
      out.push({ key, value: rec ? rec.value : raw, scope: 'session', setAt: rec?.setAt ?? null });
      seen.add(key);
    }
  } catch {
    // ignore
  }

  try {
    for (const key of readIndex(localStorage)) {
      if (seen.has(key)) continue;
      const raw = localStorage.getItem(key);
      if (raw == null) {
        removeFromIndex(localStorage, key);
        continue;
      }
      const rec = parseRecord(raw);
      if (rec && Date.now() - rec.setAt > FLAG_EXPIRY_MS) {
        localStorage.removeItem(key);
        removeFromIndex(localStorage, key);
        continue;
      }
      out.push({ key, value: rec ? rec.value : raw, scope: 'local', setAt: rec?.setAt ?? null });
    }
  } catch {
    // ignore
  }

  return out;
}

/** Clears every vharta.* override this module knows about, in both storages. */
export function clearAllVhartaOverrides(): void {
  try {
    for (const key of readIndex(localStorage)) localStorage.removeItem(key);
    localStorage.removeItem(INDEX_KEY);
  } catch {
    // ignore
  }
  try {
    for (const key of readIndex(sessionStorage)) sessionStorage.removeItem(key);
    sessionStorage.removeItem(INDEX_KEY);
  } catch {
    // ignore
  }
  bumpFlagVersion();
}

export const VHARTA_FLAG_EXPIRY_MS = FLAG_EXPIRY_MS;
export const VHARTA_FLAG_PREFIX = VHARTA_PREFIX;

/** Test-only: reset the lazy query-param-bootstrap guard between tests. */
export function __resetVhartaFlagsForTests(): void {
  queryParamsApplied = false;
}
